import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { use, useEffect, useRef } from 'react';
import { auth0Exports, useAuth } from '@/app/utils/auth';
import useIsUserOnboard, { onboardingQueryOptions } from '@/app/hooks/useIsUserOnboard';
import { useRouter } from '@tanstack/react-router';
import { Spinner } from '@/app/ui/components/spinner';
import { Navigate } from '@tanstack/react-router';
import { AppContext } from '@/app/appContext';

export const Route = createFileRoute('/_authed')({
  component: RouteComponent,
  beforeLoad: async (opts) => {
    if (!opts.context.auth.isAuthenticated) return;

    const onboarding = await opts.context.queryClient.ensureQueryData(onboardingQueryOptions());
    if (!onboarding) throw redirect({ to: '/login', search: { target: opts.location.pathname } });

    if (onboarding) {
      const { hasOrganization, hasActiveOrganization } = onboarding;

      if (
        !hasOrganization &&
        !opts.matches.some((m) => m.routeId === '/_authed/(membership)/add-organization')
      ) {
        throw redirect({ to: '/add-organization' });
      }
      if (
        hasOrganization &&
        !hasActiveOrganization &&
        !opts.matches.some((m) => m.routeId === '/_authed/(membership)/choose-organization')
      ) {
        throw redirect({ to: '/choose-organization' });
      }
    }
  },
  pendingComponent: () => <Spinner kind="inset" />,
});

function RouteComponent() {
  const { isLoading, isAuthenticated } = useAuth();
  const { pathname, search, searchStr } = useLocation({
    select: (l) => ({ pathname: l.pathname, search: l.search, searchStr: l.searchStr }),
  });

  if (isLoading) return <Spinner kind="inset" />;

  if (!isAuthenticated) {
    if (pathname === '/login') {
      const existingTarget = search.target;
      return <Navigate to={'/login'} search={existingTarget ? { target: existingTarget } : {}} />;
    }

    return <Navigate to={'/login'} search={{ target: pathname + searchStr }} />;
  }

  return <OutletWithSessionExpiredCheck />;
}

function OutletWithSessionExpiredCheck() {
  const isLoggingOut = useRef(false);
  const router = useRouter();
  const navigate = useNavigate();
  const { pathname, searchStr } = useLocation({
    select: (l) => ({ pathname: l.pathname, searchStr: l.searchStr }),
  });
  const target = pathname + searchStr;
  useIsUserOnboard();
  const { userMeta } = use(AppContext);

  const handleAuthError = async () => {
    isLoggingOut.current = true;

    const logout = auth0Exports.getLogout();
    if (logout) {
      logout(
        window.location.origin +
          router.routesByPath['/login'].fullPath +
          '?target=' +
          encodeURIComponent(target),
      );
    } else {
      navigate({ to: '/login', search: { target } });
    }
  };

  // Log the user out if the auth session has expired when he/she returns to the browser window/tab
  useEffect(() => {
    const checkSession = async () => {
      if (isLoggingOut.current) return;

      const getToken = auth0Exports.getAccessTokenSilently();
      if (getToken) {
        try {
          await getToken();
        } catch {
          void handleAuthError();
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', checkSession);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', checkSession);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (userMeta.organizationId === '') return <Spinner kind="inset" />;

  return <Outlet />;
}
